import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ShoppingCart, ArrowLeft, Package, Sparkles } from "lucide-react";
import { API } from "../constants";
import { useCart } from "../context/CartContext";

// Slug helpers so any category (with & / space / etc.) maps cleanly to a URL segment.
export const categorySlug = (c) =>
  (c || "").toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Hero styling and copy per category
const CATEGORY_META = {
  "Cleaners":                { blurb: "Cut through grime, water spots, and oxidation — restore any surface to a spotless base." },
  "Compounds":               { blurb: "Cutting compounds that erase swirls, scratches, and oxidation before the finish polish." },
  "Metal Polish":            { blurb: "Signature mirror-finish polishes for aluminum, chrome, stainless, and every metal in between." },
  "Waxes":                   { blurb: "Long-lasting carnauba and synthetic waxes for depth, gloss, and armor." },
  "Protectants":             { blurb: "Ceramic sealants and dressings that lock in the shine and repel water." },
  "Dressings":               { blurb: "Tire, trim, and rubber dressings that finish the look and keep it fresh." },
  "Interior":                { blurb: "Interior cleaners and protectants for leather, vinyl, plastic, and fabric." },
  "Kits":                    { blurb: "Ready-made bundles for detailers, first-timers, and pros who want everything in one order." },
  "Buffing Wheels":          { blurb: "Cotton, sisal, and airway wheels sized for every polishing job." },
  "Goblets & Tapers":        { blurb: "Specialty wheels and tips for hard-to-reach polishing work." },
  "Accessories":             { blurb: "The extras that make every job go faster and finish cleaner." },
  "Adapters & Accessories":  { blurb: "Shafts, spindles, adapters, and mounts that fit your polisher to every wheel we sell." },
};

const CategoryLandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recommended");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products?limit=200&in_stock=true`)
      .then(res => {
        const all = res.data || [];
        const match = all.find(p => categorySlug(p.category) === slug);
        if (!match) {
          setProducts([]);
          setCategory("");
        } else {
          setCategory(match.category);
          setProducts(all.filter(p => p.category === match.category));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  const sorted = [...products].sort((a, b) => {
    if (sort === "price-asc") return (a.online_price || 0) - (b.online_price || 0);
    if (sort === "price-desc") return (b.online_price || 0) - (a.online_price || 0);
    if (sort === "name") return (a.name || "").localeCompare(b.name || "");
    // recommended: Cut Above first, then A→Z
    const aCut = a.has_cut_above_logo ? 0 : 1;
    const bCut = b.has_cut_above_logo ? 0 : 1;
    return aCut - bCut || (a.name || "").localeCompare(b.name || "");
  });

  const meta = CATEGORY_META[category] || { blurb: "Every product we stock in this category." };
  const cutAboveCount = products.filter(p => p.has_cut_above_logo).length;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="category-landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5 bg-gradient-to-b from-[#0F0F0F] to-[#050505] pt-24 pb-14">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D62828]/50 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-[#D4AF37] text-xs uppercase tracking-widest inline-flex items-center gap-1 mb-6"
            data-testid="category-back-btn"
          ><ArrowLeft className="w-3 h-3" /> Back</button>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Category
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-4" data-testid="category-title">
            {loading ? "…" : (category || "Category not found")}
          </h1>
          <p className="text-gray-400 text-base max-w-2xl">{meta.blurb}</p>
          <div className="flex flex-wrap gap-4 mt-6 text-xs uppercase tracking-widest">
            <span className="text-gray-500"><span className="text-white font-bold text-lg">{products.length}</span> Products</span>
            {cutAboveCount > 0 && (
              <span className="text-gray-500"><span className="text-[#D4AF37] font-bold text-lg">{cutAboveCount}</span> Cut Above</span>
            )}
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-3">
        <Link to="/products" className="text-gray-400 hover:text-[#D4AF37] text-sm inline-flex items-center gap-1" data-testid="category-see-all-link">
          All products <ChevronRight className="w-4 h-4" />
        </Link>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-[#0F0F0F] border border-white/10 rounded-sm px-3 py-2 text-white text-sm"
          data-testid="category-sort-select"
        >
          <option value="recommended">Recommended</option>
          <option value="price-asc">Price · Low to High</option>
          <option value="price-desc">Price · High to Low</option>
          <option value="name">Name · A to Z</option>
        </select>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        {loading && (
          <p className="text-gray-500 text-center py-12">Loading products…</p>
        )}
        {!loading && sorted.length === 0 && (
          <div className="text-center py-16">
            <Package className="w-10 h-10 mx-auto text-gray-700 mb-3" />
            <p className="text-gray-400 mb-4">No products found for this category.</p>
            <Link to="/products" className="text-[#D62828] hover:underline">Browse all products →</Link>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {sorted.map((product, idx) => (
            <div key={product.product_id} className="group relative bg-[#0F0F0F] border border-white/5 hover:border-[#D4AF37]/40 rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1" data-testid={`category-product-${idx}`}>
              {product.has_cut_above_logo && (
                <div className="absolute top-2 left-2 z-10 bg-[#D4AF37] text-black text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-widest">CUT ABOVE</div>
              )}
              <Link to={`/products/${product.product_id}`} className="block aspect-square overflow-hidden bg-[#1A1A1A]">
                <img
                  src={product.image_url || 'https://images.unsplash.com/photo-1692486407015-3a47e4c1181c'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </Link>
              <div className="p-3">
                <Link to={`/products/${product.product_id}`}>
                  <h3 className="text-white text-sm font-semibold mb-1 line-clamp-2 group-hover:text-[#D4AF37] transition-colors min-h-[2.5rem]">
                    {product.name}
                  </h3>
                </Link>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[#D62828] font-bold">${(product.online_price || 0).toFixed(2)}</span>
                  <button
                    onClick={() => addToCart(product.product_id)}
                    className="w-8 h-8 bg-white/5 hover:bg-[#D62828] rounded-sm flex items-center justify-center transition-colors"
                    aria-label="Add to cart"
                    data-testid={`category-add-${idx}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CategoryLandingPage;
